// frontend/src/services/idpService.js
import apiClient from './apiClient';

const IDP_BASE = '/idp';

/** Default timeout aligned with backend WES_IDP_TIMEOUT (180s). */
export const DEFAULT_IDP_TIMEOUT_MS = 180_000;

/**
 * @typedef {'invalid_json' | 'http_error' | 'timeout' | 'cancelled' | 'network'} IdpServiceErrorCode
 */

export class IdpServiceError extends Error {
  /**
   * @param {IdpServiceErrorCode} code
   * @param {string} message
   * @param {{ status?: number, responseBody?: string, timeoutSeconds?: number, detail?: unknown, correlationId?: string, cause?: unknown }} [opts]
   */
  constructor(code, message, opts = {}) {
    super(message);
    this.name = 'IdpServiceError';
    this.code = code;
    this.status = opts.status;
    this.responseBody = opts.responseBody;
    this.timeoutSeconds = opts.timeoutSeconds;
    this.detail = opts.detail;
    // Hub360 error envelope carries `correlation_id`; keep it so the UI can
    // show a friendly message and still expose the ID for support (PBI 4150).
    this.correlationId = opts.correlationId;
    if (opts.cause !== undefined) {
      this.cause = opts.cause;
    }
  }
}

function messageFromErrorDetail(detail) {
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((item) => (item && typeof item.msg === 'string' ? item.msg : null))
      .filter(Boolean);
    if (msgs.length > 0) {
      return msgs.join('; ');
    }
  }
  if (detail && typeof detail === 'object') {
    const errors = detail.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      const first = errors[0];
      if (first && typeof first.message === 'string' && first.message.trim()) {
        return first.message;
      }
    }
    try {
      return JSON.stringify(detail);
    } catch {
      return null;
    }
  }
  return null;
}

function mapAxiosError(error, timeoutMs) {
  if (error?.code === 'ERR_CANCELED') {
    throw new IdpServiceError('cancelled', 'Request cancelled', { cause: error });
  }
  if (error?.code === 'ECONNABORTED') {
    throw new IdpServiceError('timeout', 'Request timeout', {
      timeoutSeconds: timeoutMs / 1000,
      cause: error,
    });
  }
  if (error?.response) {
    const { status, data } = error.response;
    const responseBody = typeof data === 'string' ? data : JSON.stringify(data);
    const envelopeMessage =
      data && typeof data === 'object' && data.error && typeof data.error.message === 'string'
        ? data.error.message
        : null;
    const detail = data && typeof data === 'object' ? data.detail ?? data : undefined;
    const structured =
      data && typeof data === 'object' && Array.isArray(data.errors) && data.errors[0]
        ? data.errors[0].message
        : null;
    const correlationId =
      data && typeof data === 'object' && typeof data.correlation_id === 'string' && data.correlation_id
        ? data.correlation_id
        : undefined;
    const msg =
      envelopeMessage
      || messageFromErrorDetail(detail)
      || structured
      || (typeof data?.detail === 'string' ? data.detail : null)
      || `HTTP ${status}`;
    throw new IdpServiceError('http_error', msg, {
      status,
      responseBody,
      detail: data,
      correlationId,
      cause: error,
    });
  }
  throw new IdpServiceError('network', error?.message || 'Network error', { cause: error });
}

/**
 * @param {{ timeoutMs?: number, signal?: AbortSignal }} [options]
 */
export async function getHealth(options = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_IDP_TIMEOUT_MS;
  try {
    const response = await apiClient.get(`${IDP_BASE}/health`, {
      timeout: timeoutMs,
      signal: options.signal,
    });
    return response.data;
  } catch (error) {
    if (error instanceof IdpServiceError) {
      throw error;
    }
    mapAxiosError(error, timeoutMs);
  }
}

/**
 * @typedef {Object} ProcessDocumentOptions
 * @property {boolean} [skipPreprocessing]
 * @property {string|null} [locale]
 * @property {'default'|'handwriting'|'print'} [preprocessProfile]
 * @property {boolean} [markHandwritingHintsInLlm]
 */

/**
 * @param {{
 *   file: File,
 *   mimeType: string,
 *   expectedClasses: string[],
 *   classificationThreshold: number,
 *   options?: ProcessDocumentOptions,
 *   timeoutMs?: number,
 *   signal?: AbortSignal,
 * }} input
 * @returns {Promise<{ data: object, rawBody: string }>}
 */
export async function processDocument(input) {
  const timeoutMs = input.timeoutMs ?? DEFAULT_IDP_TIMEOUT_MS;
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('mimeType', input.mimeType);
  formData.append('expectedClasses', JSON.stringify(input.expectedClasses));
  formData.append('classificationThreshold', String(input.classificationThreshold));
  if (input.options && Object.keys(input.options).length > 0) {
    formData.append('options', JSON.stringify(input.options));
  }

  try {
    const response = await apiClient.post(`${IDP_BASE}/documents/process`, formData, {
      timeout: timeoutMs,
      signal: input.signal,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return {
      data: response.data,
      rawBody: JSON.stringify(response.data),
    };
  } catch (error) {
    if (error instanceof IdpServiceError) {
      throw error;
    }
    mapAxiosError(error, timeoutMs);
  }
}

const idpService = {
  DEFAULT_IDP_TIMEOUT_MS,
  getHealth,
  processDocument,
};

export default idpService;
